import type { CompanyInboundSettingsApi } from '@/modules/companies/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Label } from '@/modules/ui/components/label/Label';
import { SavedIndicator } from '@/modules/ui/components/saved-indicator/SavedIndicator';
import { Switch } from '@/modules/ui/components/switch/Switch';

interface CareersPageSettingsProps {
  settings: CompanyInboundSettingsApi;
  isPending: boolean;
  isSaved: boolean;
  error: string | null;
  onToggle: (isCareersPageEnabled: boolean) => void;
  onRetry: () => void;
}

/**
 * Staff control for a company's Careers Page. The switch saves on change; the
 * slug appears once the API generates it and never changes afterwards.
 */
export function CareersPageSettings({
  settings,
  isPending,
  isSaved,
  error,
  onToggle,
  onRetry,
}: CareersPageSettingsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Careers Page</CardTitle>
        <CardDescription>
          Dev only. Lists the company&apos;s jobs that accept applications. New
          jobs start with inbound applications set to match; existing jobs are
          not changed.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
          <div className="space-y-1">
            <Label htmlFor="careers-page-toggle" className="text-base">
              Show Careers Page
            </Label>
            <p className="text-muted-foreground text-sm">
              {settings.careersPageSlug ? (
                <>
                  Path:{' '}
                  <span className="font-mono text-xs break-all">
                    /{settings.careersPageSlug}/careers
                  </span>
                </>
              ) : (
                'A URL slug is generated from the company name the first time this is turned on.'
              )}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <SavedIndicator isSaved={isSaved} />
            <Switch
              id="careers-page-toggle"
              checked={settings.isCareersPageEnabled}
              disabled={isPending}
              onCheckedChange={onToggle}
            />
          </div>
        </div>

        {error && (
          <div className="flex flex-wrap items-center gap-2">
            <InlineError>{error}</InlineError>
            <Button size="sm" variant="outline" onClick={onRetry}>
              Try again
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
